import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import * as api from '../lib/api'
import { finalSessionDeadlineDate, isHardDeadlinePassed, personLabel, StatusBadge } from './HRDashboard'
import { Alert, Card, Table, Button, Divider, Spin, Typography, Tag, Modal, Select, Space, message, Checkbox, Tabs, Tooltip, Input } from 'antd'
import { CheckCircleOutlined, ClockCircleOutlined, TeamOutlined, UserAddOutlined, UserSwitchOutlined } from '@ant-design/icons'
import ReportHistorySection from './ReportHistorySection'
import AppraisalPdfButton from './AppraisalPdfButton'
import RatingDistributionChart from './RatingDistributionChart'
import AppraisalWorkflowCard from './AppraisalWorkflowCard'
import { completedRatingColumns } from './CompletedAppraisalRatings'
import { delegationStatusPersonName, isFunctionalReviewActionable, MANAGER_DELEGATION_ENABLED } from './delegationConfig'

const { Title, Text } = Typography
const { TextArea } = Input
const MANAGER_TAB_KEYS = ['overview', 'my-appraisals', 'team-appraisals', 'direct-reports', 'past']

function daysUntil(dateStr) {
  if (!dateStr) return null
  return Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24))
}


function DeadlineBadge({ days }) {
  if (days === null) return <Text type='secondary'>—</Text>
  if (days < 0) return <Tag color='red'>Overdue by {Math.abs(days)}d</Tag>
  if (days <= 7) return <Tag color='orange'>{days}d left</Tag>
  return <Tag color='green'>{days}d left</Tag>
}

function delegationCandidateLabel(candidate) {
  const name = [candidate.first_name, candidate.last_name].filter(Boolean).join(' ').trim()
  const displayName = (name || candidate.name || '').trim()
  const displayEmail = (candidate.email || '').trim()
  if (displayName && displayEmail) return `${displayName} (${displayEmail})`
  return displayName || displayEmail || '—'
}

const DIRECT_DELEGATION = 'direct_transfer'
const INDIRECT_DELEGATION = 'functional_review'

function supportsDirectDelegation(mode) {
  return mode === DIRECT_DELEGATION || mode === 'both'
}

function supportsIndirectDelegation(mode) {
  return mode === INDIRECT_DELEGATION || mode === 'both'
}

function delegationEnabled(mode) {
  return supportsDirectDelegation(mode) || supportsIndirectDelegation(mode)
}

function approvalRouteFor(appraisal, userId) {
  if (appraisal.status === 'pending_manager' && appraisal.approver_1 === userId) return `/appraisals/${appraisal.id}/manager`
  if (appraisal.status === 'pending_head' && appraisal.approver_2 === userId) return `/appraisals/${appraisal.id}/head`
  if (appraisal.status === 'pending_bu_head' && appraisal.approver_3 === userId) return `/appraisals/${appraisal.id}/bu-head`
  if (appraisal.status === 'pending_approver_4' && appraisal.approver_4 === userId) return `/appraisals/${appraisal.id}/approver-4`
  if (appraisal.status === 'pending_approver_5' && appraisal.approver_5 === userId) return `/appraisals/${appraisal.id}/approver-5`
  return `/appraisals/${appraisal.id}`
}

function isAwaitingUserReview(appraisal, userId) {
  return approvalRouteFor(appraisal, userId) !== `/appraisals/${appraisal.id}`
}

function isBulkSignoffEligible(appraisal, userId) {
  return (
    (appraisal.status === 'pending_head' && appraisal.approver_2 === userId) ||
    (appraisal.status === 'pending_bu_head' && appraisal.approver_3 === userId) ||
    (appraisal.status === 'pending_approver_4' && appraisal.approver_4 === userId) ||
    (appraisal.status === 'pending_approver_5' && appraisal.approver_5 === userId)
  )
}

function assignedApprovalLevels(appraisal, userId) {
  return [
    appraisal.approver_1 === userId ? 1 : null,
    appraisal.approver_2 === userId ? 2 : null,
    appraisal.approver_3 === userId ? 3 : null,
    appraisal.approver_4 === userId ? 4 : null,
    appraisal.approver_5 === userId ? 5 : null
  ].filter(Boolean)
}

function wasApprovedByUser(appraisal, userId) {
  if (!userId) return false
  return (
    (appraisal.approver_1 === userId && Boolean(appraisal.manager_submitted_at)) ||
    (appraisal.approver_2 === userId && Boolean(appraisal.head_signed_off_at)) ||
    (appraisal.approver_3 === userId && Boolean(appraisal.bu_head_signed_off_at)) ||
    (appraisal.approver_4 === userId && Boolean(appraisal.approver_4_signed_off_at)) ||
    (appraisal.approver_5 === userId && Boolean(appraisal.approver_5_signed_off_at))
  )
}

const STATUS_LEVELS = {
  pending_employee: 0,
  pending_employee_review: 0,
  pending_manager: 1,
  pending_head: 2,
  pending_bu_head: 3,
  pending_approver_4: 4,
  pending_approver_5: 5,
  completed: 6
}

function currentApproverLabel(appraisal) {
  const statusLevel = STATUS_LEVELS[appraisal.status]
  const level = statusLevel >= 1 && statusLevel <= 5 ? statusLevel : 1
  return personLabel(
    appraisal[`approver_${level}_first_name`],
    appraisal[`approver_${level}_last_name`],
    appraisal[`approver_${level}_email`],
    appraisal[`approver_${level}_position`],
    appraisal.session_template
  )
}

function requestedReviews(appraisal) {
  if (appraisal.requested_functional_reviews?.length) return appraisal.requested_functional_reviews
  if (!appraisal.requested_functional_review_id) return []
  return [{
    id: appraisal.requested_functional_review_id,
    reviewer_name: appraisal.requested_functional_review_reviewer_name,
    reviewer_email: appraisal.requested_functional_review_reviewer_email,
    status: appraisal.requested_functional_review_status,
    submitted_at: appraisal.requested_functional_review_submitted_at
  }]
}

function indirectDelegationRequests(appraisal) {
  return (appraisal.delegation_request_log || []).filter(
    request => request.delegation_mode === INDIRECT_DELEGATION
  )
}

function unassignedReviewerRequests(appraisal) {
  const assignedReviewerEmails = new Set(
    requestedReviews(appraisal)
      .map(review => review.reviewer_email?.trim().toLowerCase())
      .filter(Boolean)
  )
  const seenReviewerEmails = new Set()

  return indirectDelegationRequests(appraisal).filter(request => {
    const email = request.proposed_manager_email?.trim().toLowerCase()
    if (!email) return true
    if (assignedReviewerEmails.has(email) || seenReviewerEmails.has(email)) return false
    seenReviewerEmails.add(email)
    return true
  })
}

function delegationStatusMeta(status) {
  const value = String(status || '').toLowerCase()
  if (value === 'rejected') return { label: 'Rejected', color: 'red' }
  if (value === 'pending') return { label: 'Pending', color: 'orange' }
  return { label: 'Completed', color: 'green' }
}

function feedbackTabRoute(appraisal, userId, reviewId) {
  const approvalRoute = approvalRouteFor(appraisal, userId)
  const appraisalRoute = approvalRoute !== `/appraisals/${appraisal.id}`
    ? approvalRoute
    : appraisal.approver_1 === userId
      ? `/appraisals/${appraisal.id}/manager`
      : appraisal.approver_2 === userId
        ? `/appraisals/${appraisal.id}/head`
        : appraisal.approver_3 === userId
          ? `/appraisals/${appraisal.id}/bu-head`
          : appraisal.approver_4 === userId
            ? `/appraisals/${appraisal.id}/approver-4`
            : appraisal.approver_5 === userId
              ? `/appraisals/${appraisal.id}/approver-5`
              : approvalRoute
  if (!reviewId) return appraisalRoute
  return `${appraisalRoute}/feedback/${reviewId}`
}

export default function ManagerDashboard({ appraisals = [], me, appraisalsLoading, loadAppraisals }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [delegateOpen, setDelegateOpen] = useState(false)
  const [delegating, setDelegating] = useState(false)
  const [candidateLoading, setCandidateLoading] = useState(false)
  const [delegateCandidates, setDelegateCandidates] = useState([])
  const [addCandidateOpen, setAddCandidateOpen] = useState(false)
  const [existingCandidateToAdd, setExistingCandidateToAdd] = useState(null)
  const [creatingCandidate, setCreatingCandidate] = useState(false)
  const [newCandidate, setNewCandidate] = useState({ email: '', first_name: '', last_name: '', position: '' })
  const [delegateTo, setDelegateTo] = useState(null)
  const [selectedAppraisalIds, setSelectedAppraisalIds] = useState([])
  const [bulkSignoffIds, setBulkSignoffIds] = useState([])
  const [bulkSignoffOpen, setBulkSignoffOpen] = useState(false)
  const [bulkSignoffComments, setBulkSignoffComments] = useState('')
  const [bulkSigningOff, setBulkSigningOff] = useState(false)
  const [delegationMode, setDelegationMode] = useState(DIRECT_DELEGATION)
  const [appraisalSearch, setAppraisalSearch] = useState('')
  const [departmentFilter, setDepartmentFilter] = useState(null)
  const [sessionFilter, setSessionFilter] = useState(null)
  const [statusFilter, setStatusFilter] = useState(null)
  const selectedReviewerIds = Array.isArray(delegateTo) ? delegateTo : (delegateTo ? [delegateTo] : [])
  const hasDelegateSelection = selectedReviewerIds.length > 0
  const requestedTab = searchParams.get('tab')
  const normalizedTab = requestedTab === 'delegations' ? 'team-appraisals' : requestedTab
  const activeTab = MANAGER_TAB_KEYS.includes(normalizedTab) ? normalizedTab : 'overview'

  function changeTab(key) {
    const next = new URLSearchParams(searchParams)
    next.set('tab', key)
    setSearchParams(next, { replace: true })
    loadAppraisals(true).catch(() => { })
  }

  async function loadDelegateCandidates(mode) {
    setCandidateLoading(true)
    try {
      const res = await api.getBulkDelegateCandidates(mode)
      setDelegateCandidates(res.candidates || [])
    } catch (e) {
      message.error(e.message || 'Unable to load delegate candidates')
      setDelegateCandidates([])
    } finally {
      setCandidateLoading(false)
    }
  }

  async function openDelegateModal(mode, appraisalId = null) {
    setDelegateTo(mode === INDIRECT_DELEGATION ? [] : null)
    setSelectedAppraisalIds(appraisalId ? [appraisalId] : [])
    setDelegationMode(mode)
    setDelegateOpen(true)
    await loadDelegateCandidates(mode)
  }

  async function changeDelegationMode(mode) {
    const eligibleReports = mode === INDIRECT_DELEGATION
      ? indirectDelegatableReports
      : directDelegatableReports
    const eligibleIds = new Set(eligibleReports.map(report => report.id))
    setSelectedAppraisalIds(current => current.filter(id => eligibleIds.has(id)))
    setDelegationMode(mode)
    setDelegateTo(mode === INDIRECT_DELEGATION ? [] : null)
    setDelegateCandidates([])
    await loadDelegateCandidates(mode)
  }

  function openAddCandidate() {
    if (selectedAppraisalIds.length === 0) {
      message.warning('Select at least one appraisal before adding a reviewer')
      return
    }
    setExistingCandidateToAdd(null)
    setNewCandidate({ email: '', first_name: '', last_name: '', position: '' })
    setAddCandidateOpen(true)
  }

  async function confirmAddCandidate() {
    if (selectedAppraisalIds.length === 0) {
      message.warning('Select at least one appraisal before adding a reviewer')
      return
    }
    if (existingCandidateToAdd) {
      setDelegateTo(current => delegationMode === INDIRECT_DELEGATION
        ? [...new Set([...(Array.isArray(current) ? current : []), existingCandidateToAdd])]
        : existingCandidateToAdd)
      setExistingCandidateToAdd(null)
      setAddCandidateOpen(false)
      message.success('Reviewer selected')
      return
    }
    setCreatingCandidate(true)
    try {
      const res = await api.createDelegateCandidate(selectedAppraisalIds, newCandidate, delegationMode)
      const candidate = res.candidate
      setDelegateCandidates(current => [...current.filter(item => item.id !== candidate.id), candidate])
      setDelegateTo(current => delegationMode === INDIRECT_DELEGATION
        ? [...new Set([...(Array.isArray(current) ? current : []), candidate.id])]
        : candidate.id)
      setNewCandidate({ email: '', first_name: '', last_name: '', position: '' })
      setAddCandidateOpen(false)
      message.success(res.created ? 'Reviewer created and added to the selected session(s)' : 'Existing reviewer added to the selected session(s)')
    } catch (e) {
      message.error(e.message || 'Unable to add reviewer')
    } finally {
      setCreatingCandidate(false)
    }
  }

  async function confirmDelegate() {
    if (!hasDelegateSelection || selectedAppraisalIds.length === 0 || !delegationMode) return
    setDelegating(true)
    try {
      const res = await api.bulkDelegateAppraisals(selectedAppraisalIds, delegateTo, delegationMode)
      if (res.pending_hr_approval) {
        message.success(`${res.delegation_requests || selectedAppraisalIds.length} delegation request(s) submitted to HR`)
      } else {
        const count = delegationMode === INDIRECT_DELEGATION ? (res.functional_reviews || selectedAppraisalIds.length) : (res.updated || selectedAppraisalIds.length)
        const actionLabel = delegationMode === INDIRECT_DELEGATION ? 'feedback request(s)' : 'direct delegation(s)'
        const actionVerb = delegationMode === INDIRECT_DELEGATION ? 'sent' : 'created'
        message.success(`${count} ${actionLabel} ${actionVerb} successfully`)
      }
      setDelegateOpen(false)
      setDelegateCandidates([])
      setDelegateTo(null)
      setSelectedAppraisalIds([])
      setDelegationMode(DIRECT_DELEGATION)
      await loadAppraisals(true)
    } catch (e) {
      message.error(e.message || 'Delegation failed')
    } finally {
      setDelegating(false)
    }
  }

  async function requestHrToAssignDelegate() {
    if (selectedAppraisalIds.length === 0 || !delegationMode) return
    setDelegating(true)
    try {
      const res = await api.bulkDelegateAppraisals(
        selectedAppraisalIds,
        [],
        delegationMode,
        true
      )
      message.success(`${res.delegation_requests || selectedAppraisalIds.length} delegation request(s) sent to HR for assignment`)
      setDelegateOpen(false)
      setDelegateCandidates([])
      setDelegateTo(null)
      setSelectedAppraisalIds([])
      setDelegationMode(DIRECT_DELEGATION)
      await loadAppraisals(true)
    } catch (e) {
      message.error(e.message || 'Unable to request delegation from HR')
    } finally {
      setDelegating(false)
    }
  }

  async function confirmBulkSignoff() {
    if (bulkSignoffIds.length === 0) return
    setBulkSigningOff(true)
    try {
      const result = await api.bulkApproverSignoff(bulkSignoffIds, bulkSignoffComments)
      if (result.completed > 0) {
        message.success(`${result.completed} appraisal(s) signed off`)
      }
      if (result.skipped?.length) {
        message.warning(`${result.skipped.length} appraisal(s) were skipped because they were no longer eligible`)
      }
      setBulkSignoffOpen(false)
      setBulkSignoffIds([])
      setBulkSignoffComments('')
      await loadAppraisals(true)
    } catch (e) {
      message.error(e.message || 'Bulk sign-off failed')
    } finally {
      setBulkSigningOff(false)
    }
  }

  const uniqueAppraisals = Array.from(new Map(appraisals.map(a => [a.id, a])).values())
  const activeAppraisals = uniqueAppraisals.filter(a => a.session_status === 'active')

  const myAppraisals = activeAppraisals.filter(a => a.employee === me?.id)
  const submittedMyAppraisals = myAppraisals.filter(a => a.employee_submitted_at && !['pending_employee', 'pending_employee_review'].includes(a.status))
  const reportAppraisals = activeAppraisals.filter(a => assignedApprovalLevels(a, me?.id).length > 0)
  const reportCount = new Set(reportAppraisals.map(a => a.employee)).size
  const functionalReviewAppraisals = activeAppraisals.filter(a => a.active_functional_review_id)
  const delegatedAwayAppraisals = activeAppraisals.filter(a => (
    me &&
    a.manager !== me.id &&
    (a.latest_delegation_from_manager_id === me.id || a.current_manager_access)
  ))
  const pending = reportAppraisals.filter(a => isAwaitingUserReview(a, me?.id))
  const reviewedCount = reportAppraisals.filter(appraisal => {
    const highestAssignedLevel = Math.max(...assignedApprovalLevels(appraisal, me?.id))
    return STATUS_LEVELS[appraisal.status] > highestAssignedLevel
  }).length
  const activeMyAppraisals = [...myAppraisals].sort((a, b) => (
    (['pending_employee', 'pending_employee_review'].includes(a.status) ? 0 : 1) -
    (['pending_employee', 'pending_employee_review'].includes(b.status) ? 0 : 1)
  ))
  const pendingMyAppraisals = activeMyAppraisals.filter(
    a => ['pending_employee', 'pending_employee_review'].includes(a.status) && !isHardDeadlinePassed(a)
  )
  const activeReportAppraisals = [...reportAppraisals].sort((a, b) => (
    (isAwaitingUserReview(a, me?.id) ? 0 : 1) -
    (isAwaitingUserReview(b, me?.id) ? 0 : 1)
  ))
  const delegatedToMeAppraisals = activeReportAppraisals.filter(a => (
    a.latest_delegation_to_manager_id === me?.id &&
    supportsDirectDelegation(a.session_manager_review_flow)
  ))
  const directTeamAppraisals = activeReportAppraisals.filter(a => a.latest_delegation_to_manager_id !== me?.id)
  const assignedAppraisals = Array.from(
    new Map(
      [
        ...directTeamAppraisals,
        ...delegatedAwayAppraisals,
        ...delegatedToMeAppraisals,
        ...functionalReviewAppraisals
      ].map(appraisal => [appraisal.id, appraisal])
    ).values()
  )
  const bulkSignoffEligibleAppraisals = assignedAppraisals.filter(
    appraisal => isBulkSignoffEligible(appraisal, me?.id)
  )
  const filterAppraisals = rows => {
    const search = appraisalSearch.trim().toLowerCase()
    return rows.filter(appraisal => {
      const matchesSearch = !search || [
        appraisal.employee_first_name,
        appraisal.employee_last_name,
        appraisal.employee_email,
        appraisal.employee_company_id,
        appraisal.session_name,
        appraisal.department_name,
        appraisal.approver_1_name,
        appraisal.approver_2_name,
        appraisal.approver_3_name
      ].some(value => String(value || '').toLowerCase().includes(search))
      return (
        matchesSearch &&
        (!departmentFilter || appraisal.department_name === departmentFilter) &&
        (!sessionFilter || appraisal.session === sessionFilter) &&
        (!statusFilter || appraisal.status === statusFilter)
      )
    })
  }
  const filteredAssignedAppraisals = filterAppraisals(assignedAppraisals)
  const departmentOptions = [...new Set(activeReportAppraisals.map(a => a.department_name).filter(Boolean))]
    .sort()
    .map(value => ({ label: value, value }))
  const sessionOptions = Array.from(new Map(
    activeReportAppraisals.map(a => [a.session, a.session_name])
  ).entries())
    .sort((a, b) => String(a[1]).localeCompare(String(b[1])))
    .map(([value, label]) => ({ label, value }))
  const statusOptions = [
    { label: 'Pending Employee', value: 'pending_employee' },
    { label: 'Pending Employee Review', value: 'pending_employee_review' },
    { label: 'Pending Approver 1', value: 'pending_manager' },
    { label: 'Pending Approver 2', value: 'pending_head' },
    { label: 'Pending Approver 3', value: 'pending_bu_head' },
    { label: 'Pending Approver 4', value: 'pending_approver_4' },
    { label: 'Pending Approver 5', value: 'pending_approver_5' },
    { label: 'Completed', value: 'completed' }
  ]
  const bulkSignoffEligibleIds = new Set(bulkSignoffEligibleAppraisals.map(appraisal => appraisal.id))
  const pendingFunctionalReviewAppraisals = functionalReviewAppraisals.filter(isFunctionalReviewActionable)

  const cols = [
    { title: 'Employee ID', dataIndex: 'employee_company_id', key: 'employee_id', render: value => value || '—' },
    { title: 'Employee', key: 'e', render: (_, r) => personLabel(r.employee_first_name, r.employee_last_name, r.employee_email, r.employee_position, r.session_template) },
    { title: 'Session', dataIndex: 'session_name', key: 's' },
    { title: 'Approver', key: 'mn', render: (_, r) => currentApproverLabel(r) },
    { title: 'Dept', dataIndex: 'department_name', key: 'd', render: v => v || '—' },
    { title: 'Deadline', key: 'dl', render: (_, r) => <DeadlineBadge days={daysUntil(finalSessionDeadlineDate(r))} /> },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'st',
      render: (s, r) => {
        const reviews = requestedReviews(r)
        const requests = unassignedReviewerRequests(r)
        const delegatedFromLabel = r.latest_delegation_to_manager_id === me?.id
          ? delegationStatusPersonName(r.latest_delegation_from_manager_name)
          : null

        return (
          <div style={{ width: '100%' }}>
            <div style={{ marginBottom: (reviews.length || requests.length || delegatedFromLabel) ? 6 : 0 }}>
              <StatusBadge status={s} label={r.status_label} />
            </div>

            {(reviews.length > 0 || requests.length > 0 || delegatedFromLabel) && (
              <div style={{ marginLeft: 6, borderLeft: '1px solid #d9d9d9', paddingLeft: 12 }}>
                {reviews.map(review => (
                  <Space key={review.id} size={[4, 4]} wrap>
                    <Text type='secondary'>↳</Text>
                    <Tag color='purple'>
                      Requested Feedback from: {delegationStatusPersonName(review.reviewer_name)}
                    </Tag>
                    {(() => {
                      const { label, color } = delegationStatusMeta(review.status)
                      return <Tag color={color}>{label}</Tag>
                    })()}
                    <Link to={feedbackTabRoute(r, me?.id, review.id)}>
                      <Button size='middle' type='link'>View</Button>
                    </Link>
                  </Space>
                ))}

                {requests.map(request => (
                  <Space key={`request-${request.id}`} size={[4, 4]} wrap>
                    <Text type='secondary'>↳</Text>
                    <Tag color='blue'>
                      Indirect delegation: {request.proposed_manager_name
                        ? delegationStatusPersonName(request.proposed_manager_name)
                        : 'HR to assign'}
                    </Tag>
                    {(() => {
                      const { label, color } = delegationStatusMeta(request.status)
                      return <Tag color={color}>{label}</Tag>
                    })()}
                    {request.status !== 'rejected' && (
                      <Link to={`/appraisals/${r.id}/manager`}>
                        <Button size='middle' type='link'>View</Button>
                      </Link>
                    )}
                  </Space>
                ))}

                {delegatedFromLabel && (
                  <Space size={[4, 4]} wrap>
                    <Text type='secondary'>↳</Text>
                    <Tag color='blue'>Original Manager: {delegatedFromLabel}</Tag>
                  </Space>
                )}
              </div>
            )}
          </div>
        )
      }
    },
    ...completedRatingColumns(activeReportAppraisals),
    {
      title: '',
      key: 'a',
      render: (_, r) => (
        <Space size='small' wrap>
          {isAwaitingUserReview(r, me?.id)
            ? <Link to={approvalRouteFor(r, me?.id)}><Button type='primary' size='middle'>Review</Button></Link>
            : <Link to={`/appraisals/${r.id}`}><Button size='middle' type='link'>View</Button></Link>}
          {MANAGER_DELEGATION_ENABLED && r.approver_1 === me?.id && ['pending_employee', 'pending_manager'].includes(r.status) && delegationEnabled(r.session_manager_review_flow) && (
            <Tooltip title='Delegate appraisal'>
              <Button
                size='middle'
                icon={<UserSwitchOutlined />}
                aria-label='Delegate appraisal'
                onClick={() => openDelegateModal(supportsIndirectDelegation(r.session_manager_review_flow) ? INDIRECT_DELEGATION : DIRECT_DELEGATION, r.id)}
              />
            </Tooltip>
          )}
          <AppraisalPdfButton appraisalId={r.id} status={r.status} canExport={r.can_export_pdf} type='link' size='middle' />
        </Space>
      )
    }
  ]

  const functionalReviewCols = [
    { title: 'Employee', key: 'e', render: (_, r) => personLabel(r.employee_first_name, r.employee_last_name, r.employee_email, r.employee_position, r.session_template) },
    { title: 'Employee ID', dataIndex: 'employee_company_id', key: 'employee_id', render: value => value || '—' },
    { title: 'Direct Manager', key: 'm', render: (_, r) => personLabel(r.manager_first_name, r.manager_last_name, r.manager_email, r.manager_position, r.session_template) },
    { title: 'Session', dataIndex: 'session_name', key: 's' },
    { title: 'Dept', dataIndex: 'department_name', key: 'd', render: v => v || '—' },
    { title: 'Deadline', key: 'dl', render: (_, r) => <DeadlineBadge days={daysUntil(r.functional_review_deadline_date || finalSessionDeadlineDate(r))} /> },
    ...completedRatingColumns(functionalReviewAppraisals),
    {
      title: 'Status',
      key: 'frs',
      render: (_, r) => (
        <Space size='small'>
          <StatusBadge status={r.status} label={r.status_label} />
          {!['pending_employee', 'pending_employee_review'].includes(r.status) && (
            <Tag color={r.active_functional_review_status === 'pending' ? 'purple' : 'green'}>
              Indirect delegation: {r.active_functional_review_status || '—'}
            </Tag>
          )}
        </Space>
      )
    },
    {
      title: '',
      key: 'a',
      render: (_, r) => (
        <Space size='small'>
          {isFunctionalReviewActionable(r)
            ? <Link to={`/appraisals/${r.id}/manager`}><Button type='primary' size='middle'>Review</Button></Link>
            : <Link to={feedbackTabRoute(r, me?.id, r.active_functional_review_id)}><Button size='middle' type='link'>View</Button></Link>}
          <AppraisalPdfButton appraisalId={r.id} status={r.status} canExport={r.can_export_pdf} type='link' size='middle' />
        </Space>
      )
    }
  ]

  const myAppraisalCols = [
    { title: 'Employee ID', dataIndex: 'employee_company_id', key: 'employee_id', render: value => value || '—' },
    { title: 'Employee', key: 'e', render: (_, r) => personLabel(r.employee_first_name, r.employee_last_name, r.employee_email, r.employee_position, r.session_template) },
    { title: 'Session', dataIndex: 'session_name', key: 's' },
    { title: 'Approver', key: 'mn', render: (_, r) => currentApproverLabel(r) },
    { title: 'Dept', dataIndex: 'department_name', key: 'd', render: v => v || '—' },
    { title: 'Deadline', key: 'dl', render: (_, r) => <DeadlineBadge days={daysUntil(finalSessionDeadlineDate(r))} /> },
    { title: 'Status', dataIndex: 'status', key: 'st', render: (s, r) => <StatusBadge status={s} label={r.status_label} /> },
    ...completedRatingColumns(activeMyAppraisals),
    {
      title: '',
      key: 'a',
      render: (_, r) => (
        <Space size='small'>
          {['pending_employee', 'pending_employee_review'].includes(r.status)
            ? isHardDeadlinePassed(r)
              ? <Tag color='red' style={{ margin: 0 }}>Deadline Passed</Tag>
              : <Link to={`/appraisals/${r.id}/employee`}><Button type='primary' size='middle'>{r.status === 'pending_employee_review' ? 'Review Feedback' : 'Fill Out Form'}</Button></Link>
            : <Link to={`/appraisals/${r.id}`}><Button size='middle' type='link'>View</Button></Link>}
          <AppraisalPdfButton appraisalId={r.id} status={r.status} canExport={r.can_export_pdf} type='link' size='middle' />
        </Space>
      )
    }
  ]

  const delegatableReports = activeAppraisals
    .filter(a => me && a.manager === me.id && ['pending_employee', 'pending_manager'].includes(a.status) && delegationEnabled(a.session_manager_review_flow))
    .map(a => ({
      id: a.id,
      employeeId: a.employee,
      sessionId: a.session,
      delegationMode: a.session_manager_review_flow,
      label: `${personLabel(a.employee_first_name, a.employee_last_name, a.employee_email, a.employee_position, a.session_template)} - ${a.session_name}`
    }))

  const directDelegatableReports = delegatableReports.filter(r => supportsDirectDelegation(r.delegationMode))
  const indirectDelegatableReports = delegatableReports.filter(r => supportsIndirectDelegation(r.delegationMode))
  const modalDelegatableReports = delegationMode === INDIRECT_DELEGATION ? indirectDelegatableReports : directDelegatableReports

  const directReportsMap = new Map()
  uniqueAppraisals
    .filter(a => me && (a.manager === me.id || a.current_manager_access))
    .forEach(a => {
      const existing = directReportsMap.get(a.employee)
      if (!existing) {
        directReportsMap.set(a.employee, {
          key: a.employee,
          employee_email: a.employee_email,
          employee_first_name: a.employee_first_name,
          employee_last_name: a.employee_last_name,
          employee_company_id: a.employee_company_id,
          employee_position: a.employee_position,
          department_name: a.department_name,
          activeCount: a.session_status === 'active' ? 1 : 0,
          pendingManagerCount: (a.session_status === 'active' && a.manager === me.id && a.status === 'pending_manager') ? 1 : 0,
          lastSessionName: a.session_name,
          lastSessionEndDate: a.session_end_date || ''
        })
        return
      }

      if (a.session_status === 'active') existing.activeCount += 1
      if (a.session_status === 'active' && a.manager === me.id && a.status === 'pending_manager') existing.pendingManagerCount += 1

      const currentDate = existing.lastSessionEndDate || ''
      const nextDate = a.session_end_date || ''
      if (nextDate > currentDate) {
        existing.lastSessionName = a.session_name
        existing.lastSessionEndDate = nextDate
      }

      if (!existing.department_name && a.department_name) {
        existing.department_name = a.department_name
      }
    })

  const directReports = Array.from(directReportsMap.values())
    .sort((a, b) => personLabel(a.employee_first_name, a.employee_last_name, a.employee_email, a.employee_position, a.session_template).localeCompare(personLabel(b.employee_first_name, b.employee_last_name, b.employee_email, b.employee_position, b.session_template)))

  const directReportCols = [
    { title: 'Employee', key: 'e', render: (_, r) => personLabel(r.employee_first_name, r.employee_last_name, r.employee_email, r.employee_position, r.session_template) },
    { title: 'Employee ID', dataIndex: 'employee_company_id', key: 'employee_id', render: value => value || '—' },
    { title: 'Dept', dataIndex: 'department_name', key: 'd', render: v => v || '—' },
    { title: 'Active Appraisals', dataIndex: 'activeCount', key: 'ac' },
    {
      title: 'Pending Your Review',
      dataIndex: 'pendingManagerCount',
      key: 'pm',
      render: v => <Tag color={v > 0 ? 'orange' : 'green'}>{v}</Tag>
    },
    { title: 'Latest Session', dataIndex: 'lastSessionName', key: 'ls', render: v => v || '—' }
  ]

  const selectedEmployeeIds = new Set(
    delegatableReports
      .filter(r => selectedAppraisalIds.includes(r.id))
      .map(r => r.employeeId)
  )
  const selectedSessionIds = new Set(
    delegatableReports
      .filter(r => selectedAppraisalIds.includes(r.id))
      .map(r => r.sessionId)
  )

  const availableDelegateCandidates = delegateCandidates.filter(c => {
    if (selectedEmployeeIds.has(c.id)) return false
    if (selectedSessionIds.size === 0) return true
    const candidateSessionIds = new Set(c.session_ids || [])
    return [...selectedSessionIds].every(sessionId => candidateSessionIds.has(sessionId))
  })
  const bulkSignoffRowSelection = bulkSignoffEligibleAppraisals.length > 0
    ? {
      selectedRowKeys: bulkSignoffIds,
      onChange: setBulkSignoffIds,
      getCheckboxProps: record => ({
        disabled: !bulkSignoffEligibleIds.has(record.id),
        name: `Select appraisal for ${record.employee_first_name || record.employee_email}`
      })
    }
    : undefined

  const bulkSignoffButton = bulkSignoffEligibleAppraisals.length > 0 && (
    <Button
      type='primary'
      size='small'
      disabled={bulkSignoffIds.length === 0}
      onClick={() => setBulkSignoffOpen(true)}
    >
      Bulk Sign Off{bulkSignoffIds.length > 0 ? ` (${bulkSignoffIds.length})` : ''}
    </Button>
  )

  const overviewContent = (
    <>
      <Space direction='vertical' size='middle' style={{ width: '100%' }}>
        {pendingMyAppraisals.length > 0 && (
          <Card size='small' title='Your Appraisals Requiring Action'>
            <Table
              dataSource={pendingMyAppraisals}
              columns={myAppraisalCols}
              rowKey='id'
              size='small'
              pagination={false}
              locale={{ emptyText: 'No self-appraisals currently require your action.' }}
            />
          </Card>
        )}

        {pending.length > 0 && (
          <Card size='small'>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
              <Title level={4} style={{ margin: 0 }}>Assigned Appraisals Requiring Action</Title>
              {bulkSignoffButton}
            </div>
            <Table
              dataSource={pending}
              columns={cols}
              rowKey='id'
              size='small'
              pagination={false}
              rowSelection={bulkSignoffRowSelection}
              locale={{ emptyText: 'No assigned appraisals currently require your action.' }}
            />
          </Card>
        )}

        {pendingFunctionalReviewAppraisals.length > 0 && (
          <Card size='small' title='Indirect Delegation Requests Requiring Action'>
            <Table
              dataSource={pendingFunctionalReviewAppraisals}
              columns={functionalReviewCols}
              rowKey='id'
              size='small'
              pagination={false}
              locale={{ emptyText: 'No indirect delegation requests currently require your action.' }}
            />
          </Card>
        )}

        {pendingMyAppraisals.length === 0 && pending.length === 0 && pendingFunctionalReviewAppraisals.length === 0 && (
          <Card size='small'>
            <Alert type='success' showIcon message='No appraisals currently require your action.' />
          </Card>
        )}

        {reportCount >= 5 && (
          <>
            <AppraisalWorkflowCard
              title='Assigned Appraisal Overview'
              total={reportAppraisals.length}
              completed={reviewedCount}
              statsOnly
              stats={[
                {
                  label: 'Total Assigned',
                  value: reportAppraisals.length,
                  color: 'var(--tti-info)',
                  icon: <TeamOutlined style={{ color: 'var(--tti-info)', fontSize: 18 }} />
                },
                {
                  label: 'Awaiting Your Approval',
                  value: pending.length,
                  color: pending.length > 0 ? 'var(--tti-warning)' : 'var(--tti-success)',
                  icon: <ClockCircleOutlined style={{ color: pending.length > 0 ? 'var(--tti-warning)' : 'var(--tti-success)', fontSize: 18 }} />
                },
                {
                  label: 'Reviewed',
                  value: reviewedCount,
                  color: 'var(--tti-success)',
                  icon: <CheckCircleOutlined style={{ color: 'var(--tti-success)', fontSize: 18 }} />
                }
              ]}
            />
            <RatingDistributionChart scope='assigned' />
          </>
        )}
      </Space>
    </>
  )

  const myAppraisalsContent = (
    <>

      <Card size='small'>
        <Title level={4} style={{ marginBottom: 16 }}>My Appraisals</Title>
        {submittedMyAppraisals.map(appraisal => (
          <Alert
            key={`submitted-${appraisal.id}`}
            type='success'
            showIcon
            style={{ marginBottom: 16 }}
            message={`${appraisal.session_name} was submitted successfully`}
            description='Your PA form is now in the approval process. No further action is needed unless it is returned to you.'
          />
        ))}
        <Table
          dataSource={activeMyAppraisals}
          columns={myAppraisalCols}
          rowKey='id'
          size='small'
          pagination={false}
          locale={{ emptyText: 'No active appraisals assigned to you.' }}
        />
      </Card>
    </>
  )

  const teamAppraisalsContent = (
    <Space direction='vertical' size='middle' style={{ width: '100%' }}>
      <Card size='small'>
        <Title level={4} style={{ marginBottom: 16 }}>Search and Filter Appraisals</Title>
        <Space size='small' wrap style={{ width: '100%' }}>
          <Input.Search
            allowClear
            placeholder='Search employee, email, ID, session, department, or approver'
            value={appraisalSearch}
            onChange={event => setAppraisalSearch(event.target.value)}
            style={{ width: 360 }}
            suffix={null}
          />
          <Select
            allowClear
            placeholder='All departments'
            value={departmentFilter}
            onChange={setDepartmentFilter}
            options={departmentOptions}
            style={{ minWidth: 180 }}
          />
          <Select
            allowClear
            placeholder='All sessions'
            value={sessionFilter}
            onChange={setSessionFilter}
            options={sessionOptions}
            style={{ minWidth: 180 }}
          />
          <Select
            allowClear
            placeholder='All statuses'
            value={statusFilter}
            onChange={setStatusFilter}
            options={statusOptions}
            style={{ minWidth: 180 }}
          />
        </Space>
      </Card>
      <Card
        size='small'
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
          <Title level={4} style={{ margin: 0 }}>Assigned Appraisals</Title>
          {(
            <Space size='small'>
              {bulkSignoffButton}
              {MANAGER_DELEGATION_ENABLED && directDelegatableReports.length > 0 && (
                <Button type='primary' size='small' onClick={() => openDelegateModal(DIRECT_DELEGATION)}>
                  Request Direct Delegation
                </Button>
              )}
              {MANAGER_DELEGATION_ENABLED && indirectDelegatableReports.length > 0 && (
                <Button type='primary' size='small' onClick={() => openDelegateModal(INDIRECT_DELEGATION)}>
                  Request Indirect Delegation
                </Button>
              )}
            </Space>
          )}
        </div>
        
        <Table
          dataSource={filteredAssignedAppraisals}
          columns={cols}
          rowKey='id'
          size='small'
          pagination={false}
          rowSelection={bulkSignoffRowSelection}
          locale={{ emptyText: 'No active appraisals are assigned to you.' }}
        />
      </Card>
    </Space>
  )

  const delegationModal = (
    <>
      <Modal
        title={delegationMode === INDIRECT_DELEGATION ? 'Request Indirect Delegation' : 'Request Direct Delegation'}
        open={delegateOpen}
        onCancel={() => {
          setDelegateOpen(false)
          setDelegateCandidates([])
          setDelegateTo(null)
          setSelectedAppraisalIds([])
          setDelegationMode(DIRECT_DELEGATION)
        }}
        onOk={confirmDelegate}
        okButtonProps={{ disabled: !hasDelegateSelection || selectedAppraisalIds.length === 0 || !delegationMode, loading: delegating }}
      >
        <Text type='secondary'>
          {delegationMode === INDIRECT_DELEGATION
            ? 'Request HR approval for additional feedback while keeping PA ownership as the direct manager.'
            : 'Request HR approval to transfer PA ownership to another manager for the selected appraisals.'}
        </Text>

        <div style={{ marginTop: 14 }}>
          <Text strong>Delegation type</Text>
          <Select
            value={delegationMode}
            onChange={changeDelegationMode}
            style={{ width: '100%', marginTop: 6 }}
            options={[
              directDelegatableReports.length > 0 && { value: DIRECT_DELEGATION, label: 'Direct - transfer Approver 1 ownership' },
              indirectDelegatableReports.length > 0 && { value: INDIRECT_DELEGATION, label: 'Indirect - request reviewer feedback' }
            ].filter(Boolean)}
          />
        </div>

        <div style={{ marginTop: 12, maxHeight: 260, overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: 6, padding: 10 }}>
          {modalDelegatableReports.length === 0
            ? <Text type='secondary'>No pending direct reports available for this delegation type.</Text>
            : (
              <Checkbox.Group
                style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
                value={selectedAppraisalIds}
                onChange={(vals) => {
                  setSelectedAppraisalIds(vals)
                  const employeeIds = new Set(modalDelegatableReports.filter(r => vals.includes(r.id)).map(r => r.employeeId))
                  if (selectedReviewerIds.some(id => employeeIds.has(id))) {
                    setDelegateTo(delegationMode === INDIRECT_DELEGATION ? selectedReviewerIds.filter(id => !employeeIds.has(id)) : null)
                  }
                }}
                options={modalDelegatableReports.map(r => ({ label: r.label, value: r.id }))}
              />
            )}
        </div>

        <div style={{ marginTop: 14 }}>
          <Text strong>{delegationMode === INDIRECT_DELEGATION ? 'Selected Reviewers' : 'Selected Reviewer'}</Text>
          <div style={{ minHeight: 40, marginTop: 6, padding: 8, border: '1px solid #d9d9d9', borderRadius: 6 }}>
            {selectedReviewerIds.length > 0
              ? selectedReviewerIds.map(id => {
                const candidate = delegateCandidates.find(item => item.id === id)
                return (
                  <Tag
                    key={id}
                    closable
                    onClose={() => setDelegateTo(current => delegationMode === INDIRECT_DELEGATION
                      ? (Array.isArray(current) ? current : []).filter(value => value !== id)
                      : null)}
                  >
                    {candidate ? delegationCandidateLabel(candidate) : `Reviewer ${id}`}
                  </Tag>
                )
              })
              : <Text type='secondary'>No reviewer selected</Text>}
          </div>
          <Button
            type='dashed'
            block
            icon={<UserAddOutlined />}
            style={{ marginTop: 10 }}
            onClick={openAddCandidate}
          >
            Add Reviewer
          </Button>
          <Button
            className='tti-btn-secondary'
            style={{ marginTop: 10 }}
            loading={delegating}
            disabled={selectedAppraisalIds.length === 0 || !delegationMode}
            onClick={requestHrToAssignDelegate}
          >
            Request HR to Choose a Delegate
          </Button>
        </div>
      </Modal>
      <Modal
        title='Add Reviewer'
        open={addCandidateOpen}
        onCancel={() => {
          setAddCandidateOpen(false)
          setExistingCandidateToAdd(null)
        }}
        onOk={confirmAddCandidate}
        okText={existingCandidateToAdd ? 'Select Reviewer' : 'Create and Select Reviewer'}
        okButtonProps={{
          type: 'primary',
          loading: creatingCandidate,
          disabled: !existingCandidateToAdd && (!newCandidate.email.trim() || !newCandidate.first_name.trim() || !newCandidate.last_name.trim())
        }}
        cancelButtonProps={{ className: 'tti-btn-secondary' }}
      >
        <Space direction='vertical' size='middle' style={{ width: '100%' }}>
          <Text strong>Select an Existing Reviewer</Text>
          <Select
            allowClear
            showSearch
            style={{ width: '100%' }}
            placeholder={candidateLoading ? 'Loading reviewers...' : 'Search existing reviewers'}
            loading={candidateLoading}
            value={existingCandidateToAdd}
            onChange={setExistingCandidateToAdd}
            options={availableDelegateCandidates
              .filter(candidate => !selectedReviewerIds.includes(candidate.id))
              .map(candidate => ({
                value: candidate.id,
                label: delegationCandidateLabel(candidate),
                email: candidate.email
              }))}
            filterOption={(input, option) => `${option?.label || ''} ${option?.email || ''}`.toLowerCase().includes(input.toLowerCase())}
          />
          <Divider plain>or create a new reviewer</Divider>
          <Input
            type='email'
            placeholder='Reviewer email'
            disabled={Boolean(existingCandidateToAdd)}
            value={newCandidate.email}
            onChange={event => setNewCandidate(current => ({ ...current, email: event.target.value }))}
          />
          <Space.Compact style={{ width: '100%' }}>
            <Input
              placeholder='First name'
              disabled={Boolean(existingCandidateToAdd)}
              value={newCandidate.first_name}
              onChange={event => setNewCandidate(current => ({ ...current, first_name: event.target.value }))}
            />
            <Input
              placeholder='Last name'
              disabled={Boolean(existingCandidateToAdd)}
              value={newCandidate.last_name}
              onChange={event => setNewCandidate(current => ({ ...current, last_name: event.target.value }))}
            />
          </Space.Compact>
          <Input
            placeholder='Position (optional)'
            disabled={Boolean(existingCandidateToAdd)}
            value={newCandidate.position}
            onChange={event => setNewCandidate(current => ({ ...current, position: event.target.value }))}
          />
        </Space>
      </Modal>
    </>
  )

  const tabContent = content => appraisalsLoading
    ? <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}><Spin /></div>
    : content

  return (
    <div>
      <Tabs
        activeKey={activeTab}
        onChange={changeTab}
        items={[
          { key: 'overview', label: 'Overview', children: tabContent(overviewContent) },
          { key: 'my-appraisals', label: 'My Own Appraisals', children: tabContent(myAppraisalsContent) },
          { key: 'team-appraisals', label: 'My Report Appraisals', children: tabContent(teamAppraisalsContent) },
        ]}
      />
      {MANAGER_DELEGATION_ENABLED && delegationModal}
      <Modal
        title='Bulk Sign Off Appraisals'
        open={bulkSignoffOpen}
        okText={`Sign Off ${bulkSignoffIds.length} Appraisal${bulkSignoffIds.length === 1 ? '' : 's'}`}
        okButtonProps={{ loading: bulkSigningOff, disabled: bulkSignoffIds.length === 0 }}
        cancelButtonProps={{ disabled: bulkSigningOff }}
        closable={!bulkSigningOff}
        maskClosable={false}
        onOk={confirmBulkSignoff}
        onCancel={() => {
          setBulkSignoffOpen(false)
          setBulkSignoffComments('')
        }}
      >
        <Alert
          type='warning'
          showIcon
          message='Confirm bulk sign-off'
          description='Selected appraisals will advance to the next assigned approver or be completed. Review each appraisal before signing off.'
          style={{ marginBottom: 16 }}
        />
        <Text type='secondary'>Optional comments applied to every selected appraisal</Text>
        <TextArea
          rows={4}
          value={bulkSignoffComments}
          onChange={event => setBulkSignoffComments(event.target.value)}
          placeholder='Add shared approver comments'
          style={{ marginTop: 8 }}
        />
      </Modal>
    </div>
  )
}
